import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
  AmountInput,
  Button,
  Checkbox,
  Chip,
  CurrenciesUSD,
  Dropdown,
  DropdownOption,
  Icon,
  Input,
  Modal,
  ModalSize,
  SegmentedControl,
  SegmentedControlSize,
  ToggleHeight,
} from '@column/column-ui-kit';

import { normalizeTransferTypes, toggleInArray } from '~/app/pages/Transfers/_utils/filterUtils';
import { DateInput } from '~/components/DateInput';
import { Search } from '~/components/Search';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { TransferStatus, formatTransferStatus } from '~/lib/transfers';
import { TransferStatusObject, TransferTypeOptions } from '~/repositories/Transfer/TransferRepository';
import { Flex, Text } from '~/styles';
import { formatString } from '~/util';
import { convertDateToRange, convertDatesToRange, parseDateTime } from '~/util/dateFilterHelpers';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 var(--page-margin) 24px;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    padding-top: 8px;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const TransferTypes = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 -8px;
  padding: 8px 0 0;

  ${Text} {
    text-transform: capitalize;
  }
`;

const TimeWrapper = styled.div`
  position: relative;
  min-height: 16px;
`;

const TimeButton = styled(Button)<{ $isHidden?: boolean }>`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  transition:
    opacity 0.2s,
    color 0.2s;

  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

const TimeContent = styled(ToggleHeight)`
  --toggle-height-padding: 0;
`;

const TimeFields = styled.div`
  display: grid;
  grid-template-columns: auto 83px;
  grid-gap: 8px;
  align-items: center;
`;

const CheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.1s;

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(50)};
  }
`;

const FilterSearch = styled(Search)`
  max-width: 100%;

  label {
    width: 100%;
  }
`;

interface TransferFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  bankAccountList?: DropdownOption[];
  bankAccountHiddenOptions?: DropdownOption[];
  searchBankAccounts?: (description: string) => void;
  hideAccount?: boolean;
  initialBankAccountId?: string | undefined;
  initialAmount?: { amount?: number; amountGte?: number; amountLte?: number } | undefined;
  initialType?: { isIncoming?: boolean; type?: string[] } | undefined;
  initialStatus?: string[] | undefined;
  initialDate?: { createdAtGte?: string; createdAtLt?: string } | undefined;
  initialFilterByIdValue?: string | undefined;
  onApply?: (values: {
    bankAccountId?: string;
    amount?: number;
    amountGte?: number;
    amountLte?: number;
    isIncoming?: boolean;
    type?: string[];
    status?: string[];
    createdAtGte?: string;
    createdAtLt?: string;
  }) => void;
  onFilterById?: (value: string) => void;
}

type FormValues = {
  bankAccountId?: string;
  amount?: number;
  amountGte?: number;
  amountLte?: number;
  isIncoming?: boolean;
  type?: (typeof TransferTypeOptions)[number][];
  status?: string[];
  uiCurrentTab?: 'exact' | 'range';
  uiDate?: { date?: string; time?: string; timeAm?: boolean };
  uiStartDate?: { date?: string; time?: string; timeAm?: boolean };
  uiEndDate?: { date?: string; time?: string; timeAm?: boolean };
  uiDateShowTime?: boolean;
  uiStartDateShowTime?: boolean;
  uiEndDateShowTime?: boolean;
  uiFilterByIdValue?: string;
};

export const TransferFiltersModal: React.FC<TransferFiltersModalProps> = ({
  isOpen,
  onClose,
  bankAccountList,
  bankAccountHiddenOptions,
  searchBankAccounts,
  hideAccount,
  initialBankAccountId,
  initialAmount,
  initialType,
  initialStatus,
  initialDate,
  initialFilterByIdValue,
  onApply,
  onFilterById,
}) => {
  const enableTransferApprovals = useFeatureFlag(FeatureFlag.EnableTransferApprovals);

  const buildDefaults = (): FormValues => {
    const base: FormValues = {
      bankAccountId: initialBankAccountId,
      amount: initialAmount?.amount,
      amountGte: initialAmount?.amountGte,
      amountLte: initialAmount?.amountLte,
      isIncoming: initialType?.isIncoming,
      type: normalizeTransferTypes(initialType?.type as unknown[] | undefined),
      status: initialStatus ?? [],
      uiFilterByIdValue: initialFilterByIdValue ?? '',
      uiCurrentTab: 'exact',
      uiDate: {},
      uiStartDate: {},
      uiEndDate: {},
      uiDateShowTime: false,
      uiStartDateShowTime: false,
      uiEndDateShowTime: false,
    };

    if (initialDate?.createdAtGte && initialDate?.createdAtLt) {
      const startInfo = parseDateTime(initialDate.createdAtGte);
      const endInfo = parseDateTime(initialDate.createdAtLt, true);
      if (startInfo.date === endInfo.date) {
        base.uiDate = startInfo;
        base.uiDateShowTime = true;
        base.uiStartDate = {};
        base.uiEndDate = {};
        base.uiCurrentTab = 'exact';
      } else {
        base.uiDate = {};
        base.uiStartDate = startInfo;
        base.uiStartDateShowTime = true;
        base.uiEndDate = endInfo;
        base.uiEndDateShowTime = true;
        base.uiCurrentTab = 'range';
      }
    }

    return base;
  };

  const methods = useForm<FormValues>({
    defaultValues: buildDefaults(),
  });

  const { watch, setValue, reset, getValues } = methods;

  useEffect(() => {
    if (!isOpen) return;
    reset(buildDefaults());
  }, [
    isOpen,
    initialBankAccountId,
    initialAmount,
    initialType,
    initialStatus,
    initialDate,
    initialFilterByIdValue,
    reset,
  ]);

  const statusOptions = useMemo(() => {
    const array: DropdownOption[] = [];
    for (const key in TransferStatusObject) {
      if (TransferStatusObject.hasOwnProperty(key)) {
        array.push({ label: formatString(key).toUpperCase(), isDivider: true });
        const values = TransferStatusObject[key as keyof typeof TransferStatusObject];
        values.forEach((entry) => {
          if (!enableTransferApprovals && entry === 'pre_review') return;
          array.push({
            label: formatTransferStatus(entry as TransferStatus),
            value: entry,
            onClick: () => {
              const current = new Set(watch('status') ?? []);
              if (current.has(entry)) {
                current.delete(entry);
              } else {
                current.add(entry);
              }
              setValue('status', Array.from(current));
            },
          });
        });
      }
    }
    return array;
  }, [enableTransferApprovals, watch, setValue]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Medium}>
      <Modal.Header title="Filters" noBorder />
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={16}>
          {hideAccount !== true && (bankAccountList?.length || 0) > 0 && (
            <Section>
              <Label>Account</Label>
              <Dropdown
                options={bankAccountList ?? []}
                hiddenOptions={bankAccountHiddenOptions}
                active={watch('bankAccountId')}
                label={bankAccountHiddenOptions?.find((b) => b.value === watch('bankAccountId'))?.label}
                search
                size="small"
                fullWidth
                placeholder="Select Account..."
                searchLabel="Search for description"
                onSearchChange={(description: string) => searchBankAccounts?.(description)}
                onChange={(value) => setValue('bankAccountId', value)}
              />
            </Section>
          )}

          <Section>
            <Label>Filter by ID</Label>
            <FilterSearch
              value={watch('uiFilterByIdValue') ?? ''}
              onFilterSubmit={(value) => onFilterById?.(value)}
              placeholder="Filter by ID"
              onValueChange={(v) => setValue('uiFilterByIdValue', v)}
            />
          </Section>

          <Section>
            <Label>Amount</Label>
            <Flex flexDirection="column" gap={16}>
              <AmountInput
                value={watch('amount')}
                onChange={(value) => setValue('amount', value && value > 0 ? value : undefined)}
                fixedCurrencyCode="USD"
                currencyList={CurrenciesUSD}
              />
              <Flex gap={12}>
                <Flex flexDirection="column" gap={8} auto>
                  <Label>Minimum</Label>
                  <AmountInput
                    value={watch('amountGte')}
                    onChange={(value) => setValue('amountGte', value && value > 0 ? value : undefined)}
                    fixedCurrencyCode="USD"
                    currencyList={CurrenciesUSD}
                  />
                </Flex>
                <Flex flexDirection="column" gap={8} auto>
                  <Label>Maximum</Label>
                  <AmountInput
                    value={watch('amountLte')}
                    onChange={(value) => setValue('amountLte', value && value > 0 ? value : undefined)}
                    fixedCurrencyCode="USD"
                    currencyList={CurrenciesUSD}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Section>

          <Section>
            <Label>Direction</Label>
            <SegmentedControl
              options={[
                { label: 'All', value: 'all' },
                { label: 'Incoming', value: true },
                { label: 'Outgoing', value: false },
              ]}
              fullWidth
              size={SegmentedControlSize.Small}
              active={watch('isIncoming') === undefined ? 'all' : watch('isIncoming')}
              onOptionChange={(value) => setValue('isIncoming', value === 'all' ? undefined : (value as boolean))}
            />
          </Section>

          <Section>
            <Label>Type</Label>
            <TransferTypes>
              {TransferTypeOptions.map((entry) => {
                const current = (watch('type') ?? []) as (typeof TransferTypeOptions)[number][];
                const checked = current.includes(entry);
                const toggle = () => setValue('type', normalizeTransferTypes(toggleInArray(current, entry)));
                return (
                  <CheckItem key={entry} onClick={toggle}>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox isChecked={checked} onCheckedChange={toggle} />
                    </div>
                    <Text weight="medium" size={14} color="foreground">
                      {formatString(entry)
                        .replaceAll('Ach', 'ACH')
                        .replaceAll('Swift', 'International Wire')
                        .replaceAll('Check credit', 'Deposited Check')
                        .replaceAll('Check debit', 'Issued Check')}
                    </Text>
                  </CheckItem>
                );
              })}
            </TransferTypes>
          </Section>

          <Section>
            <Label>Status</Label>
            <Flex flexDirection="column" gap={8}>
              <Flex gap={8}>
                {[...new Set(watch('status') ?? [])].map((entry) => (
                  <Chip key={entry} icon={<Icon.Cross />}>
                    <Flex alignItems="center" gap={4}>
                      {formatTransferStatus(entry as TransferStatus)}
                      <Button
                        size="tiny"
                        variant="subtle"
                        icon={<Icon.Cross />}
                        onClick={() =>
                          setValue(
                            'status',
                            (watch('status') ?? []).filter((s) => s !== entry)
                          )
                        }
                      />
                    </Flex>
                  </Chip>
                ))}
              </Flex>
              <Dropdown
                search
                active={watch('status')}
                options={statusOptions}
                size="small"
                renderInside
                fullWidth
                label="Add status..."
              />
            </Flex>
          </Section>

          <Section>
            <Label>Date</Label>
            <Flex flexDirection="column" gap={12}>
              <SegmentedControl
                size={SegmentedControlSize.Small}
                options={[
                  { label: 'Exact', value: 'exact' },
                  { label: 'Range', value: 'range' },
                ]}
                fullWidth
                active={watch('uiCurrentTab') ?? 'exact'}
                onOptionChange={(v) => setValue('uiCurrentTab', v as 'exact' | 'range')}
              />
              {(watch('uiCurrentTab') ?? 'exact') !== 'range' ? (
                <>
                  <DateInput
                    size="small"
                    value={watch('uiDate')?.date}
                    onChange={(value) => setValue('uiDate', { ...(watch('uiDate') ?? {}), date: value })}
                    renderInside
                  />
                  <TimeWrapper>
                    <TimeButton
                      variant="subtle-primary"
                      size="small"
                      icon={<Icon.Plus />}
                      onClick={() => setValue('uiDateShowTime', true)}
                      $isHidden={watch('uiDateShowTime')}
                    >
                      Add time
                    </TimeButton>
                    <TimeContent isClose={!watch('uiDateShowTime')}>
                      <Label>Time</Label>
                      <TimeFields>
                        <Input
                          placeholder="00:00"
                          value={watch('uiDate')?.time ?? ''}
                          onChange={(value) =>
                            setValue('uiDate', {
                              ...(watch('uiDate') ?? {}),
                              time: value.replace(/[^0-9:]/g, '').slice(0, 5),
                            })
                          }
                          size="small"
                        />
                        <SegmentedControl
                          options={[
                            { label: 'AM', value: true },
                            { label: 'PM', value: false },
                          ]}
                          size={SegmentedControlSize.Small}
                          active={watch('uiDate')?.timeAm ?? true}
                          onOptionChange={(value: boolean | undefined) =>
                            setValue('uiDate', { ...(watch('uiDate') ?? {}), timeAm: value })
                          }
                        />
                      </TimeFields>
                    </TimeContent>
                  </TimeWrapper>
                </>
              ) : (
                <>
                  <Label>Start Date</Label>
                  <DateInput
                    size="small"
                    value={watch('uiStartDate')?.date}
                    onChange={(value) => setValue('uiStartDate', { ...(watch('uiStartDate') ?? {}), date: value })}
                    renderInside
                  />
                  <TimeWrapper>
                    <TimeButton
                      variant="subtle-primary"
                      size="small"
                      icon={<Icon.Plus />}
                      onClick={() => setValue('uiStartDateShowTime', true)}
                      $isHidden={watch('uiStartDateShowTime')}
                    >
                      Add time
                    </TimeButton>
                    <TimeContent isClose={!watch('uiStartDateShowTime')}>
                      <Label>Time</Label>
                      <TimeFields>
                        <Input
                          placeholder="00:00"
                          value={watch('uiStartDate')?.time ?? ''}
                          onChange={(value) =>
                            setValue('uiStartDate', {
                              ...(watch('uiStartDate') ?? {}),
                              time: value.replace(/[^0-9:]/g, '').slice(0, 5),
                            })
                          }
                          size="small"
                        />
                        <SegmentedControl
                          options={[
                            { label: 'AM', value: true },
                            { label: 'PM', value: false },
                          ]}
                          size={SegmentedControlSize.Small}
                          active={watch('uiStartDate')?.timeAm ?? true}
                          onOptionChange={(value: boolean | undefined) =>
                            setValue('uiStartDate', { ...(watch('uiStartDate') ?? {}), timeAm: value })
                          }
                        />
                      </TimeFields>
                    </TimeContent>
                  </TimeWrapper>
                  <Label>End Date</Label>
                  <DateInput
                    size="small"
                    value={watch('uiEndDate')?.date}
                    onChange={(value) => setValue('uiEndDate', { ...(watch('uiEndDate') ?? {}), date: value })}
                    renderInside
                  />
                  <TimeWrapper>
                    <TimeButton
                      variant="subtle-primary"
                      size="small"
                      icon={<Icon.Plus />}
                      onClick={() => setValue('uiEndDateShowTime', true)}
                      $isHidden={watch('uiEndDateShowTime')}
                    >
                      Add time
                    </TimeButton>
                    <TimeContent isClose={!watch('uiEndDateShowTime')}>
                      <Label>Time</Label>
                      <TimeFields>
                        <Input
                          placeholder="00:00"
                          value={watch('uiEndDate')?.time ?? ''}
                          onChange={(value) =>
                            setValue('uiEndDate', {
                              ...(watch('uiEndDate') ?? {}),
                              time: value.replace(/[^0-9:]/g, '').slice(0, 5),
                            })
                          }
                          size="small"
                        />
                        <SegmentedControl
                          options={[
                            { label: 'AM', value: true },
                            { label: 'PM', value: false },
                          ]}
                          size={SegmentedControlSize.Small}
                          active={watch('uiEndDate')?.timeAm ?? true}
                          onOptionChange={(value: boolean | undefined) =>
                            setValue('uiEndDate', { ...(watch('uiEndDate') ?? {}), timeAm: value })
                          }
                        />
                      </TimeFields>
                    </TimeContent>
                  </TimeWrapper>
                </>
              )}
            </Flex>
          </Section>

          <Modal.Footer>
            <Button size="small" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="small"
              onClick={() => {
                const fv = getValues();
                const datePayload =
                  (fv.uiCurrentTab ?? 'exact') === 'exact'
                    ? convertDateToRange(fv.uiDate?.date, fv.uiDate?.time, fv.uiDate?.timeAm)
                    : convertDatesToRange({
                        startDateString: fv.uiStartDate?.date,
                        startTime: fv.uiStartDate?.time,
                        startTimeAM: fv.uiStartDate?.timeAm,
                        endDateString: fv.uiEndDate?.date,
                        endTime: fv.uiEndDate?.time,
                        endTimeAM: fv.uiEndDate?.timeAm,
                      });
                onApply?.({
                  bankAccountId: fv.bankAccountId,
                  amount: fv.amount,
                  amountGte: fv.amountGte,
                  amountLte: fv.amountLte,
                  isIncoming: fv.isIncoming,
                  type: (fv.type ?? []) as unknown as string[],
                  status: fv.status,
                  createdAtGte: datePayload.createdAtGte,
                  createdAtLt: datePayload.createdAtLt,
                });
                onClose();
              }}
            >
              Apply
            </Button>
          </Modal.Footer>
        </Flex>
      </FormProvider>
    </Modal>
  );
};
